import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import bus from "@/bus";

export const PlaybackBannerActions = {
    Seek: "Seek",
    Play: "Play",
    Pause: "Pause",

    ReplayBanner: "ReplayBanner"
} as const;

export const PlaybackBannerEvents = {
    Init: "Init",
    Remove: "Remove",
    SetBannerTime: "SetTime",
    SetBannerDuration: "SetDuration"
} as const;

export const BannerNamespace = {
    MasterTemplates: "masterTemplates",
    Preview: "preview",
    Edit: "edit",
    Qa: "qa",
    Variants: "variants",
    Library: "library",
    NewSizeModal: "newSizeModal"
} as const;

export function usePlaybackController(playbackNamespace: string) {
    const isPlaying = ref(true);
    const currentPlaybackTime = ref(0);

    const banners = ref<{ id: string; duration: number }[]>([]);

    const duration = computed(() => banners.value[0]?.duration ?? 0);
    const playbackControllerId = computed(() => banners.value[0]?.id ?? "");
    const isEnded = computed(() => currentPlaybackTime.value >= duration.value);

    watch(isEnded, () => {
        if (isEnded.value) {
            pause();
        }
    });

    onBeforeMount(() => {
        bus.$on(PlaybackBannerEvents.Init, initBanner);
        bus.$on(PlaybackBannerEvents.Remove, removeBanner);
        bus.$on(PlaybackBannerEvents.SetBannerTime, setBannerTime);
        bus.$on(PlaybackBannerEvents.SetBannerDuration, setBannerDuration);

        /**
         * playback may be initiated directly with the functions below or the global event,
         * so we listen here to catch both cases
         */
        bus.$on(PlaybackBannerActions.Play, setIsPlaying);
        bus.$on(PlaybackBannerActions.Pause, unsetIsPlaying);
    });

    onBeforeUnmount(() => {
        bus.$off(PlaybackBannerEvents.Init, initBanner);
        bus.$off(PlaybackBannerEvents.Remove, removeBanner);
        bus.$off(PlaybackBannerEvents.SetBannerTime, setBannerTime);
        bus.$off(PlaybackBannerEvents.SetBannerDuration, setBannerDuration);

        bus.$off(PlaybackBannerActions.Play, setIsPlaying);
        bus.$off(PlaybackBannerActions.Pause, unsetIsPlaying);
    });

    /**
     * we sort banners by duration in descending order
     * and we use the first banner as the timeline duration (since it's the longest)
     */
    function sortBanners() {
        banners.value.sort((a, b) => b.duration - a.duration);
    }

    function setIsPlaying() {
        isPlaying.value = true;
    }

    function unsetIsPlaying() {
        isPlaying.value = false;
    }

    /* GLOBAL EVENT LISTENERS */
    function initBanner({ namespace, id }: { namespace: string; id: string }) {
        if (namespace !== playbackNamespace) {
            return;
        }

        banners.value.push({ id, duration: 0 });
        sortBanners();
    }

    function removeBanner({ namespace, id }: { namespace: string; id: string }) {
        if (namespace !== playbackNamespace) {
            return;
        }

        const index = banners.value.findIndex(banner => banner.id === id);

        if (index !== -1) {
            banners.value.splice(index, 1);
            sortBanners();
        }

        if (!banners.value.length) {
            currentPlaybackTime.value = 0;
        }
    }

    function setBannerTime({ namespace, id, currentTime }: { namespace: string; id: string; currentTime: number }) {
        if (namespace !== playbackNamespace) {
            return;
        }

        if (playbackControllerId.value === id) {
            currentPlaybackTime.value = currentTime;
        }
    }

    function setBannerDuration({ namespace, id, duration }: { namespace: string; id: string; duration: number }) {
        if (namespace !== playbackNamespace) {
            return;
        }

        const index = banners.value.findIndex(banner => banner.id === id);

        if (index !== -1) {
            banners.value[index].duration = duration;
            sortBanners();
        }
    }

    /* PLAYBACK CONTROL */
    function seek(time: number) {
        currentPlaybackTime.value = time;

        pause();
        bus.$emit(PlaybackBannerActions.Seek, { namespace: playbackNamespace, time });
    }

    function play() {
        if (isEnded.value) {
            replay();

            return;
        }

        bus.$emit(PlaybackBannerActions.Play, { namespace: playbackNamespace });
    }

    function pause() {
        bus.$emit(PlaybackBannerActions.Pause, { namespace: playbackNamespace });
    }

    function replay() {
        seek(0);
        play();
    }

    function playPause() {
        if (isPlaying.value) {
            pause();
        } else {
            play();
        }
    }

    return {
        isPlaying,
        currentTime: currentPlaybackTime,
        duration,

        seek,
        play,
        pause,
        replay,
        playPause
    };
}
